import { Skeleton } from '@/components/ui/skeleton';
import { useFilteredNews } from '@/hooks/useNews';
import { Filter, NewsItem as NewsItemType } from '@/lib/types';
import React from 'react';
import NewsItem from './NewsItem';

interface TimeOrderedNewsProps {
  filter: Filter;
  onSelectItem: (item: NewsItemType) => void;
  selectedItem: NewsItemType | null;
  topNewsCount: number;
  currentPage: number;
  pageSize: number;
}

const TimeOrderedNews: React.FC<TimeOrderedNewsProps> = ({
  filter,
  onSelectItem,
  selectedItem,
  topNewsCount,
  currentPage,
  pageSize
}) => {
  const PAGE_SIZE = 50; // Fetch 50 items per page (much smaller than 1000)
  const { data: news, isLoading, error } = useFilteredNews(filter, currentPage, PAGE_SIZE);

  if (error) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="terminal-section-header">
          Time Ordered News
        </div>
        <div className="p-4 text-[#FF4500]">
          Error loading news: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  // Calculate pagination
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedNews = news ? news.slice(startIndex, startIndex + pageSize) : [];

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="terminal-section-header flex justify-between items-center">
        <div className="flex items-center">
          <span>Time Ordered News</span>
          {Object.keys(filter).length > 0 && (
            <span className="ml-2 text-xs font-normal text-[#999]">
              ({filter.provider || ''} {filter.region || ''})
            </span>
          )}
        </div>
        {news && news.length > 0 && (
          <span className="text-xs font-normal text-[#666]">
            Showing {startIndex + 1}-{Math.min(startIndex + pageSize, news.length)} of {news.length}
          </span>
        )}
      </div>

      <div className="flex flex-col overflow-auto">
        {isLoading ? (
          // Show skeletons while loading
          Array(10).fill(0).map((_, i) => (
            <div key={i} className="news-item">
              <div className="news-id">{i + topNewsCount + 1})</div>
              <Skeleton className="h-3.5 w-full bg-gray-800" />
              <div className="news-provider">
                <Skeleton className="h-3.5 w-10 bg-gray-800" />
              </div>
              <div className="news-time">
                <Skeleton className="h-3.5 w-10 bg-gray-800" />
              </div>
            </div>
          ))
        ) : (
          paginatedNews.map((item, index) => (
            <NewsItem
              key={item.id}
              item={item}
              index={startIndex + index + topNewsCount + 1}
              isSelected={selectedItem?.id === item.id}
              onClick={onSelectItem}
            />
          ))
        )}
      </div>

      {news?.length === 0 && !isLoading && (
        <div className="px-4 py-2 text-terminal">
          No news available with the current filters.
        </div>
      )}
    </div>
  );
};

export default TimeOrderedNews;
